// Primary navigation.
//
// Two distinct surfaces are rendered from this one component:
//
//   - Mobile bottom nav (variant="bottom"): a flat row of the five most-used
//     routes. Capped at five because anything more loses tap accuracy and
//     adds visual noise.
//
//   - Desktop sidebar (variant="sidebar"): Home pinned at the top, three
//     grouped sections (Workspaces, Travel, Programs), and a bottom-pinned
//     block holding the Admin Console link (for `systemAdmin` users) followed
//     by Notifications (visible to everyone). The grouping mirrors the section
//     structure on the Home tile grid so both surfaces agree on where each
//     module lives.
//
// Notifications live in the desktop sidebar as a bottom-pinned entry with an
// unread badge (IMP-021). On mobile the bell stays in the top Header instead.

import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import {
  Home,
  Users,
  KeyRound,
  Calendar,
  Wrench,
  ShieldAlert,
  Building2,
  ClipboardList,
  Receipt,
  Car,
  Link as LinkIcon,
  Plane,
  Hotel,
  ExternalLink,
  Bell,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { CAP } from '@onlogic/shared';
import { useAuth } from '../lib/auth';
import { useFeatureFlags, isFeatureEnabled, type FeatureFlags } from '../lib/features';
import { useUnreadNotificationCount } from './NotificationBell';

interface BottomNavProps {
  variant: 'bottom' | 'sidebar';
  className?: string;
}

// Outbound booking partner — Engine is OnLogic's hotel booking platform.
// Members portal entry point; opens in a new tab (external Tile).
const ENGINE_URL = 'https://members.engine.com/';

// Mobile bottom nav: flat list of the five most-used routes. Purchase
// Requests is included here independent of its sidebar group (Programs)
// because frequency of use, not taxonomy, drives the bottom-tab selection.
// It took Events' slot: on mobile, events stay one tap away through the
// Upcoming events card and the Events tile on Home.
const MOBILE_NAV_ITEMS = [
  { to: '/', label: 'Home', Icon: Home, end: true, feature: null },
  { to: '/doors', label: 'Doors', Icon: KeyRound, end: false, feature: null },
  { to: '/directory', label: 'Directory', Icon: Users, end: false, feature: null },
  { to: '/purchase-requests', label: 'Purchase', Icon: Receipt, end: false, feature: 'purchaseRequests' },
  { to: '/tickets', label: 'Facilities', Icon: Wrench, end: false, feature: 'facilities' },
] as const;

// Desktop sidebar groups. Each group renders a small uppercase header above
// its items. `external: true` items render as anchor tags (target="_blank")
// instead of NavLink so they open in a new tab without triggering the SPA
// router.
type GroupItem = {
  to: string;
  label: string;
  // Lucide icons declare `size?: string | number`, so use lucide's own
  // LucideIcon type. A hand-rolled ComponentType that narrowed size to
  // `number` rejected every icon component (TS2322).
  Icon: LucideIcon;
  // null = always visible; otherwise gated by the named feature flag.
  feature: keyof FeatureFlags | null;
  external?: boolean;
};

const SIDEBAR_GROUPS: ReadonlyArray<{ label: string; items: ReadonlyArray<GroupItem> }> = [
  {
    label: 'Workspaces',
    items: [
      { to: '/doors', label: 'Doors', Icon: KeyRound, feature: null },
      { to: '/directory', label: 'Directory', Icon: Users, feature: null },
      { to: '/offices', label: 'Offices', Icon: Building2, feature: null },
      { to: '/tickets', label: 'Facilities', Icon: Wrench, feature: 'facilities' },
      { to: '/resources', label: 'Resources', Icon: LinkIcon, feature: null },
    ],
  },
  {
    label: 'Travel',
    items: [
      // Car Rental Discounts were folded into the Book a Car page (IMP-024),
      // so there's no standalone Travel entry for them anymore.
      { to: '/cars', label: 'Book a Car', Icon: Car, feature: 'cars' },
      { to: '/aircraft', label: 'Aircraft', Icon: Plane, feature: 'aircraft' },
      { to: ENGINE_URL, label: 'Hotels (Engine)', Icon: Hotel, feature: null, external: true },
    ],
  },
  {
    label: 'Programs',
    items: [
      { to: '/purchase-requests', label: 'Purchase Requests', Icon: Receipt, feature: 'purchaseRequests' },
      { to: '/events', label: 'Events', Icon: Calendar, feature: 'events' },
      { to: '/surveys', label: 'Surveys', Icon: ClipboardList, feature: 'surveys' },
    ],
  },
];

export function BottomNav({ variant, className }: BottomNavProps) {
  const { claims } = useAuth();
  const { flags } = useFeatureFlags();

  // Centralised feature-flag visibility check used by both surfaces.
  const isVisible = (feature: GroupItem['feature']): boolean => {
    if (!feature) return true;
    return isFeatureEnabled(feature, flags, claims);
  };

  const isSystemAdmin = claims?.capabilities?.includes(CAP.systemAdmin) ?? false;

  // ─── Mobile bottom nav ───────────────────────────────────────────────
  if (variant === 'bottom') {
    const visibleMobile = MOBILE_NAV_ITEMS.filter((item) => isVisible(item.feature));
    return (
      <nav className={clsx('flex h-16', className)}>
        {visibleMobile.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              clsx(
                'flex flex-1 flex-col items-center justify-center gap-0.5 px-2 text-xs',
                isActive ? 'text-orange font-bold' : 'text-steel hover:text-carbon',
              )
            }
          >
            <Icon size={22} aria-hidden />
            <span className="leading-tight">{label}</span>
          </NavLink>
        ))}
      </nav>
    );
  }

  // ─── Desktop sidebar ─────────────────────────────────────────────────
  // Outer element is a <div> (not <nav>) because AppShell already wraps this
  // component in a <nav> — nested <nav>s are invalid HTML.
  return (
    <div className={clsx('flex h-full flex-col gap-1', className)}>
      {/* Home — standalone at the top, ungrouped */}
      <NavLink
        to="/"
        end
        className={({ isActive }) =>
          clsx(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            isActive ? 'bg-orange/10 text-orange font-bold' : 'text-steel hover:bg-n-50 hover:text-carbon',
          )
        }
      >
        <Home size={18} aria-hidden />
        <span>Home</span>
      </NavLink>

      {/* Grouped sections: Workspaces / Travel / Programs */}
      {SIDEBAR_GROUPS.map((group) => {
        const visibleItems = group.items.filter((item) => isVisible(item.feature));
        if (visibleItems.length === 0) return null;
        return (
          <div key={group.label} className="mt-3">
            <div className="px-3 mb-1.5 text-overline font-bold uppercase tracking-wider text-steel">
              {group.label}
            </div>
            {visibleItems.map((item) => (
              <SidebarItem key={`${group.label}-${item.to}`} item={item} />
            ))}
          </div>
        );
      })}

      {/* Bottom-pinned block — Admin Console (systemAdmin only), followed by
          Notifications (everyone). The Audit & Access Logs viewer is reachable
          from inside the Admin Console, so it's no longer a separate menu entry
          (IMP-049 tweak). mt-auto pushes the block to the bottom of the flex
          column when content fits; if the sidebar overflows it scrolls with the
          rest. No top divider — the bottom group has no horizontal rule. */}
      <div className="mt-auto pt-2">
        {isSystemAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              clsx(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive ? 'bg-orange/10 text-orange font-bold' : 'text-steel hover:bg-n-50 hover:text-carbon',
              )
            }
          >
            <ShieldAlert size={18} aria-hidden />
            <span>Admin Console</span>
          </NavLink>
        )}
        <SidebarNotificationsItem />
      </div>
    </div>
  );
}

/**
 * Desktop sidebar "Notifications" entry (IMP-021). Lives in its own component
 * so the unread-count Firestore subscription only runs when the sidebar is
 * mounted (the mobile bottom-nav variant returns before this renders), and the
 * mobile Header bell keeps its own subscription. The unread badge sits on the
 * bell icon, mirroring NotificationBell.
 */
function SidebarNotificationsItem() {
  const unreadCount = useUnreadNotificationCount();
  return (
    <NavLink
      to="/notifications"
      className={({ isActive }) =>
        clsx(
          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-orange/10 text-orange font-bold' : 'text-steel hover:bg-n-50 hover:text-carbon',
        )
      }
    >
      <span className="relative flex items-center">
        <Bell size={18} aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-orange text-overline font-bold text-white ring-2 ring-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </span>
      <span>Notifications</span>
    </NavLink>
  );
}

/**
 * Renders a single sidebar entry. Internal routes use NavLink (so the active
 * state highlights when the user is on the page); external links use an
 * anchor with target="_blank" plus rel="noopener noreferrer" — never give an
 * untrusted destination access to window.opener.
 */
function SidebarItem({ item }: { item: GroupItem }) {
  const baseClasses =
    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';

  if (item.external) {
    return (
      <a
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(baseClasses, 'text-steel hover:bg-n-50 hover:text-carbon')}
      >
        <item.Icon size={18} aria-hidden />
        <span className="flex-1">{item.label}</span>
        <ExternalLink size={12} aria-hidden className="text-steel/60" />
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        clsx(
          baseClasses,
          isActive ? 'bg-orange/10 text-orange font-bold' : 'text-steel hover:bg-n-50 hover:text-carbon',
        )
      }
    >
      <item.Icon size={18} aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}
